/**
 * Search Service - Provides unified search functionality across all data types
 */

import { observationsApi } from '../api/observations';
import { assessmentsApi } from '../api/assessments';
import { childrenApi } from '../api/children';
import { classroomsApi } from '../api/classrooms';
import { Observation, Assessment, Child, Classroom } from '../../types/api';

export interface SearchFilters {
  query?: string;
  dateRange?: {
    start: string;
    end: string;
  };
  categories?: string[];
  learningAreas?: string[];
  children?: string[];
  classrooms?: string[];
  grades?: string[];
  teachers?: string[];
  isMilestone?: boolean;
  isConcern?: boolean;
  isPublished?: boolean;
  contentTypes?: SearchContentType[];
  sortBy?: 'relevance' | 'date' | 'title' | 'updated';
  sortOrder?: 'asc' | 'desc';
}

export type SearchContentType = 'observations' | 'assessments' | 'children' | 'classrooms';

export interface SearchResult<T = any> {
  id: string;
  type: SearchContentType;
  title: string;
  description: string;
  date: string;
  relevanceScore: number;
  highlights: string[];
  metadata: Record<string, any>;
  data: T;
}

export interface SearchResponse {
  results: SearchResult[];
  totalCount: number;
  facets: {
    categories: Array<{ name: string; count: number }>;
    learningAreas: Array<{ name: string; count: number }>;
    children: Array<{ name: string; count: number }>;
    classrooms: Array<{ name: string; count: number }>;
    contentTypes: Array<{ name: string; count: number }>;
  };
  suggestions: string[];
  searchTime: number;
}

export interface SavedSearch {
  id: string;
  name: string;
  filters: SearchFilters;
  createdAt: string;
  isDefault?: boolean;
}

class SearchService {
  private static instance: SearchService;
  private recentSearches: string[] = [];
  private savedSearches: SavedSearch[] = [];
  private searchHistory: Array<{ query: string; timestamp: number; resultCount: number }> = [];

  private constructor() {
    this.loadSavedData();
  }

  static getInstance(): SearchService {
    if (!SearchService.instance) {
      SearchService.instance = new SearchService();
    }
    return SearchService.instance;
  }

  /**
   * Perform unified search across all content types
   */
  async search(filters: SearchFilters, options: { limit?: number; offset?: number } = {}): Promise<SearchResponse> {
    const startTime = Date.now();
    const { limit = 50, offset = 0 } = options;
    
    try {
      // Build promises for each content type
      const searchPromises: Promise<SearchResult[]>[] = [];
      const contentTypes = filters.contentTypes || ['observations', 'assessments', 'children', 'classrooms'];

      if (contentTypes.includes('observations')) {
        searchPromises.push(this.searchObservations(filters));
      }
      
      if (contentTypes.includes('assessments')) {
        searchPromises.push(this.searchAssessments(filters));
      }
      
      if (contentTypes.includes('children')) {
        searchPromises.push(this.searchChildren(filters));
      }
      
      if (contentTypes.includes('classrooms')) {
        searchPromises.push(this.searchClassrooms(filters));
      }

      // Execute all searches concurrently
      const resultSets = await Promise.all(searchPromises);
      const allResults = resultSets.flat();

      // Sort by relevance and apply pagination
      const sortedResults = this.sortResults(allResults, filters.sortBy, filters.sortOrder);
      const paginatedResults = sortedResults.slice(offset, offset + limit);

      // Build facets from all results (before pagination)
      const facets = this.buildFacets(allResults);

      // Generate suggestions
      const suggestions = await this.generateSuggestions(filters.query || '');

      // Record search in history
      if (filters.query) {
        this.addToHistory(filters.query, allResults.length);
      }

      const searchTime = Date.now() - startTime;

      return {
        results: paginatedResults,
        totalCount: allResults.length,
        facets,
        suggestions,
        searchTime,
      };

    } catch (error) {
      console.error('Search failed:', error);
      throw new Error('Search operation failed');
    }
  }

  /**
   * Search observations
   */
  private async searchObservations(filters: SearchFilters): Promise<SearchResult[]> {
    try {
      const params: any = {
        limit: 1000, // Get more for better relevance scoring
      };

      // Apply filters
      if (filters.query) {
        params.search = filters.query;
      }
      
      if (filters.dateRange) {
        params.date_from = filters.dateRange.start;
        params.date_to = filters.dateRange.end;
      }

      if (filters.categories?.length) {
        params.category = filters.categories;
      }

      if (filters.children?.length) {
        params.child_id = filters.children;
      }

      if (filters.classrooms?.length) {
        params.classroom_id = filters.classrooms;
      }

      if (filters.isMilestone !== undefined) {
        params.is_milestone = filters.isMilestone;
      }

      if (filters.isConcern !== undefined) {
        params.is_concern = filters.isConcern;
      }

      const response = await observationsApi.getObservations(params);
      
      return response.items.map((observation: Observation) => ({
        id: observation.id,
        type: 'observations' as const,
        title: observation.title || 'Untitled Observation',
        description: observation.content || '',
        date: observation.observation_date,
        relevanceScore: this.calculateRelevanceScore(observation, filters.query || ''),
        highlights: this.extractHighlights(observation, filters.query || ''),
        metadata: {
          category: observation.category,
          learningArea: observation.learning_area,
          isMilestone: observation.is_milestone,
          isConcern: observation.is_concern,
          childName: observation.child?.name,
          teacherName: observation.teacher?.first_name,
        },
        data: observation,
      }));

    } catch (error) {
      console.error('Observation search failed:', error);
      return [];
    }
  }

  /**
   * Search assessments
   */
  private async searchAssessments(filters: SearchFilters): Promise<SearchResult[]> {
    try {
      const params: any = {
        limit: 1000,
      };

      // Apply filters
      if (filters.dateRange) {
        params.date_from = filters.dateRange.start;
        params.date_to = filters.dateRange.end;
      }

      if (filters.learningAreas?.length) {
        params.learning_area_id = filters.learningAreas;
      }

      if (filters.children?.length) {
        params.child_id = filters.children;
      }

      if (filters.classrooms?.length) {
        params.classroom_id = filters.classrooms;
      }

      if (filters.isPublished !== undefined) {
        params.is_published = filters.isPublished;
      }

      const response = await assessmentsApi.getAssessments(params);
      
      return response.items.map((assessment: Assessment) => ({
        id: assessment.id,
        type: 'assessments' as const,
        title: assessment.learning_outcome?.title || 'Assessment',
        description: assessment.notes || '',
        date: assessment.assessment_date,
        relevanceScore: this.calculateRelevanceScore(assessment, filters.query || ''),
        highlights: this.extractHighlights(assessment, filters.query || ''),
        metadata: {
          score: assessment.score,
          learningArea: assessment.learning_area?.name,
          outcome: assessment.learning_outcome?.title,
          isPublished: assessment.is_published,
          childName: assessment.child?.name,
        },
        data: assessment,
      }));

    } catch (error) {
      console.error('Assessment search failed:', error);
      return [];
    }
  }

  /**
   * Search children
   */
  private async searchChildren(filters: SearchFilters): Promise<SearchResult[]> {
    try {
      const params: any = {
        limit: 1000,
      };

      if (filters.query) {
        params.search = filters.query;
      }

      if (filters.classrooms?.length) {
        params.classroom_id = filters.classrooms;
      }

      const response = await childrenApi.getChildren(params);
      
      return response.items.map((child: Child) => ({
        id: child.id,
        type: 'children' as const,
        title: `${child.name} ${child.surname}`,
        description: child.notes || '',
        date: child.created_at,
        relevanceScore: this.calculateRelevanceScore(child, filters.query || ''),
        highlights: this.extractHighlights(child, filters.query || ''),
        metadata: {
          age: child.age,
          gender: child.gender,
          isActive: child.is_active,
          classroomName: child.classroom?.name,
        },
        data: child,
      }));

    } catch (error) {
      console.error('Children search failed:', error);
      return [];
    }
  }

  /**
   * Search classrooms
   */
  private async searchClassrooms(filters: SearchFilters): Promise<SearchResult[]> {
    try {
      const params: any = {
        limit: 1000,
      };

      if (filters.grades?.length) {
        params.grade_id = filters.grades;
      }

      if (filters.teachers?.length) {
        params.teacher_id = filters.teachers;
      }

      const response = await classroomsApi.getClassrooms(params);
      
      return response.map((classroom: Classroom) => ({
        id: classroom.id,
        type: 'classrooms' as const,
        title: classroom.name,
        description: classroom.description || '',
        date: classroom.created_at,
        relevanceScore: this.calculateRelevanceScore(classroom, filters.query || ''),
        highlights: this.extractHighlights(classroom, filters.query || ''),
        metadata: {
          gradeName: typeof classroom.grade === 'string' ? classroom.grade : classroom.grade?.name,
          teacherName: classroom.teacher?.first_name,
          capacity: classroom.capacity,
          academicYear: classroom.academic_year,
          isActive: classroom.is_active,
        },
        data: classroom,
      }));

    } catch (error) {
      console.error('Classroom search failed:', error);
      return [];
    }
  }

  /**
   * Calculate relevance score for search results
   */
  private calculateRelevanceScore(item: any, query: string): number {
    if (!query) return 1;

    const queryLower = query.toLowerCase();
    let score = 0;

    // Check title/name matches (highest weight)
    const title = (item.title || item.name || '').toLowerCase();
    if (title.includes(queryLower)) {
      score += title === queryLower ? 100 : 50;
    }

    // Check content/description matches
    const content = (item.content || item.description || item.notes || '').toLowerCase();
    if (content.includes(queryLower)) {
      score += 20;
    }

    // Check category/type matches
    const category = (item.category || item.learning_area?.name || '').toLowerCase();
    if (category.includes(queryLower)) {
      score += 30;
    }

    // Boost recent items
    if (item.created_at || item.observation_date || item.assessment_date) {
      const date = new Date(item.created_at || item.observation_date || item.assessment_date);
      const daysSinceCreated = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceCreated < 7) score += 10;
      if (daysSinceCreated < 30) score += 5;
    }

    // Boost milestones and concerns
    if (item.is_milestone) score += 15;
    if (item.is_concern) score += 10;

    return Math.max(score, 1);
  }

  /**
   * Extract highlights from search results
   */
  private extractHighlights(item: any, query: string): string[] {
    if (!query) return [];

    const highlights: string[] = [];
    const queryLower = query.toLowerCase();

    // Check various fields for highlights
    const fields = [
      item.title || item.name,
      item.content || item.description || item.notes,
      item.category,
      item.learning_area?.name,
    ].filter(Boolean);

    fields.forEach(field => {
      if (typeof field === 'string' && field.toLowerCase().includes(queryLower)) {
        const index = field.toLowerCase().indexOf(queryLower);
        const start = Math.max(0, index - 30);
        const end = Math.min(field.length, index + query.length + 30);
        let excerpt = field.slice(start, end);
        
        if (start > 0) excerpt = '...' + excerpt;
        if (end < field.length) excerpt = excerpt + '...';
        
        highlights.push(excerpt);
      }
    });

    return highlights.slice(0, 3); // Limit to 3 highlights
  }

  /**
   * Sort search results
   */
  private sortResults(
    results: SearchResult[],
    sortBy: string = 'relevance',
    sortOrder: string = 'desc'
  ): SearchResult[] {
    return [...results].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'relevance':
          comparison = a.relevanceScore - b.relevanceScore;
          break;
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        default:
          comparison = a.relevanceScore - b.relevanceScore;
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }

  /**
   * Build facets from search results
   */
  private buildFacets(results: SearchResult[]) {
    const facets = {
      categories: new Map<string, number>(),
      learningAreas: new Map<string, number>(),
      children: new Map<string, number>(),
      classrooms: new Map<string, number>(),
      contentTypes: new Map<string, number>(),
    };

    results.forEach(result => {
      // Count content types
      facets.contentTypes.set(
        result.type,
        (facets.contentTypes.get(result.type) || 0) + 1
      );

      // Count categories
      if (result.metadata.category) {
        facets.categories.set(
          result.metadata.category,
          (facets.categories.get(result.metadata.category) || 0) + 1
        );
      }

      // Count learning areas
      if (result.metadata.learningArea) {
        facets.learningAreas.set(
          result.metadata.learningArea,
          (facets.learningAreas.get(result.metadata.learningArea) || 0) + 1
        );
      }

      // Count children
      if (result.metadata.childName) {
        facets.children.set(
          result.metadata.childName,
          (facets.children.get(result.metadata.childName) || 0) + 1
        );
      }

      // Count classrooms
      if (result.metadata.classroomName) {
        facets.classrooms.set(
          result.metadata.classroomName,
          (facets.classrooms.get(result.metadata.classroomName) || 0) + 1
        );
      }
    });

    // Convert to array format and sort by count
    return {
      categories: Array.from(facets.categories.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
      learningAreas: Array.from(facets.learningAreas.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
      children: Array.from(facets.children.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
      classrooms: Array.from(facets.classrooms.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
      contentTypes: Array.from(facets.contentTypes.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
    };
  }

  /**
   * Generate search suggestions
   */
  private async generateSuggestions(query: string): Promise<string[]> {
    const suggestions: string[] = [];
    
    // Add recent searches that match
    this.recentSearches.forEach(recent => {
      if (recent.toLowerCase().includes(query.toLowerCase()) && recent !== query) {
        suggestions.push(recent);
      }
    });

    // Add common search patterns
    const commonSuggestions = [
      'milestone observations',
      'concerns this week',
      'math assessments',
      'recent observations',
      'high scoring assessments',
      'classroom activities',
    ];

    commonSuggestions.forEach(suggestion => {
      if (suggestion.toLowerCase().includes(query.toLowerCase()) && !suggestions.includes(suggestion)) {
        suggestions.push(suggestion);
      }
    });

    return suggestions.slice(0, 5);
  }

  /**
   * Recent searches management
   */
  getRecentSearches(): string[] {
    return [...this.recentSearches];
  }

  addToRecentSearches(query: string): void {
    if (!query.trim()) return;
    
    // Remove if already exists
    this.recentSearches = this.recentSearches.filter(q => q !== query);
    
    // Add to beginning
    this.recentSearches.unshift(query);
    
    // Keep only last 10
    this.recentSearches = this.recentSearches.slice(0, 10);
    
    this.saveData();
  }

  clearRecentSearches(): void {
    this.recentSearches = [];
    this.saveData();
  }

  /**
   * Search history management
   */
  addToHistory(query: string, resultCount: number): void {
    this.searchHistory.unshift({
      query,
      timestamp: Date.now(),
      resultCount,
    });
    
    // Keep only last 100
    this.searchHistory = this.searchHistory.slice(0, 100);
    this.saveData();
  }

  getSearchHistory() {
    return [...this.searchHistory];
  }

  /**
   * Saved searches management
   */
  saveSearch(name: string, filters: SearchFilters): SavedSearch {
    const savedSearch: SavedSearch = {
      id: `search_${Date.now()}`,
      name,
      filters,
      createdAt: new Date().toISOString(),
    };

    this.savedSearches.push(savedSearch);
    this.saveData();
    
    return savedSearch;
  }

  getSavedSearches(): SavedSearch[] {
    return [...this.savedSearches];
  }

  deleteSavedSearch(id: string): void {
    this.savedSearches = this.savedSearches.filter(search => search.id !== id);
    this.saveData();
  }

  /**
   * Data persistence
   */
  private loadSavedData(): void {
    try {
      const saved = localStorage.getItem('sprout_search_data');
      if (saved) {
        const data = JSON.parse(saved);
        this.recentSearches = data.recentSearches || [];
        this.savedSearches = data.savedSearches || [];
        this.searchHistory = data.searchHistory || [];
      }
    } catch (error) {
      console.warn('Failed to load search data:', error);
    }
  }

  private saveData(): void {
    try {
      localStorage.setItem('sprout_search_data', JSON.stringify({
        recentSearches: this.recentSearches,
        savedSearches: this.savedSearches,
        searchHistory: this.searchHistory,
      }));
    } catch (error) {
      console.warn('Failed to save search data:', error);
    }
  }

  /**
   * Drops every persisted search artefact from this device. Called from
   * the auth-store logout / clearAuth path so child names typed into the
   * search box don't outlive the session on a shared device (POPIA s19,
   * minimisation).
   */
  clearAllData(): void {
    this.recentSearches = [];
    this.savedSearches = [];
    this.searchHistory = [];
    try {
      localStorage.removeItem('sprout_search_data');
    } catch (error) {
      console.warn('Failed to clear search data:', error);
    }
  }
}

export default SearchService.getInstance();